/**
 * Pure geometry functions for converting Overpass API responses to GeoJSON.
 *
 * Extracted from cairn-map.ts for testability and reuse.
 */

/** A single coordinate point from the Overpass API */
export interface OverpassCoord {
  lat: number
  lon: number
}

/** A way element from the Overpass API */
export interface OverpassWayElement {
  type: 'way'
  geometry?: OverpassCoord[]
  [key: string]: unknown
}

/** A member of a relation element */
export interface OverpassRelationMember {
  type: string
  role: string
  geometry?: OverpassCoord[]
}

/** A relation element from the Overpass API */
export interface OverpassRelationElement {
  type: 'relation'
  members?: OverpassRelationMember[]
  [key: string]: unknown
}

/** Any element returned by the Overpass API */
export type OverpassElement = OverpassWayElement | OverpassRelationElement | Record<string, unknown>

/**
 * Convert an Overpass API element to a GeoJSON Feature.
 *
 * - Way elements become LineString (open) or Polygon (closed ring).
 * - Relation elements with outer members become Polygon or MultiPolygon.
 * - Relations without outer ways become MultiLineString.
 *
 * Returns null if the element cannot be converted.
 */
export function overpassToGeoJSON(element: Record<string, unknown>): GeoJSON.Feature | null {
  if (element.type === 'way' && Array.isArray(element.geometry)) {
    const coords = (element.geometry as Array<{ lat: number; lon: number }>).map(
      p => [p.lon, p.lat] as [number, number]
    )
    if (coords.length < 2) return null

    const isClosed =
      coords.length >= 4 &&
      coords[0][0] === coords[coords.length - 1][0] &&
      coords[0][1] === coords[coords.length - 1][1]

    return {
      type: 'Feature',
      geometry: isClosed
        ? { type: 'Polygon', coordinates: [coords] }
        : { type: 'LineString', coordinates: coords },
      properties: {},
    }
  }

  if (element.type === 'relation' && Array.isArray(element.members)) {
    const members = element.members as Array<{
      type: string
      role: string
      geometry?: Array<{ lat: number; lon: number }>
    }>

    const outerSegments: Array<[number, number][]> = []
    const innerRings: Array<[number, number][]> = []

    for (const member of members) {
      if (member.type !== 'way' || !member.geometry) continue
      const coords = member.geometry.map(p => [p.lon, p.lat] as [number, number])
      if (coords.length < 2) continue

      if (member.role === 'outer' || member.role === '') {
        outerSegments.push(coords)
      } else if (member.role === 'inner') {
        innerRings.push(coords)
      }
    }

    if (outerSegments.length > 0) {
      const closedOuters = assembleClosedRings(outerSegments)

      const polygons: Array<[number, number][][]> = closedOuters.map(ring => [ring])

      for (const inner of innerRings) {
        const ownerIdx = findContainingRing(closedOuters, inner)
        if (ownerIdx >= 0) {
          polygons[ownerIdx].push(inner)
        } else {
          polygons[0].push(inner)
        }
      }

      if (polygons.length === 1) {
        return {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: polygons[0] },
          properties: {},
        }
      }

      return {
        type: 'Feature',
        geometry: { type: 'MultiPolygon', coordinates: polygons },
        properties: {},
      }
    }

    // Relation with no outer ways (e.g., route relation) — draw as lines
    const allCoords: Array<[number, number][]> = []
    for (const member of members) {
      if (member.type !== 'way' || !member.geometry) continue
      allCoords.push(member.geometry.map(p => [p.lon, p.lat] as [number, number]))
    }
    if (allCoords.length === 0) return null

    return {
      type: 'Feature',
      geometry: { type: 'MultiLineString', coordinates: allCoords },
      properties: {},
    }
  }

  return null
}

/**
 * Stitch segments into one or more closed rings.
 *
 * Uses greedy nearest-neighbor to join segments end-to-end. When the
 * current ring closes (tail meets head), it's finalized and any remaining
 * segments start a new ring. This correctly handles multipolygon relations
 * where outer ways belong to separate disconnected polygons.
 */
export function assembleClosedRings(
  segments: Array<[number, number][]>
): Array<[number, number][]> {
  if (segments.length === 0) return []
  if (segments.length === 1) return [segments[0]]

  const remaining = segments.map(s => [...s])
  const rings: Array<[number, number][]> = []

  while (remaining.length > 0) {
    const ring = remaining.splice(0, 1)[0]

    while (remaining.length > 0) {
      if (isClosed(ring)) break

      const lastPoint = ring[ring.length - 1]
      let bestIdx = -1
      let bestReverse = false
      let bestDist = Infinity

      for (let i = 0; i < remaining.length; i++) {
        const seg = remaining[i]
        const distStart = Math.abs(lastPoint[0] - seg[0][0]) + Math.abs(lastPoint[1] - seg[0][1])
        const distEnd =
          Math.abs(lastPoint[0] - seg[seg.length - 1][0]) +
          Math.abs(lastPoint[1] - seg[seg.length - 1][1])

        if (distStart < bestDist) {
          bestDist = distStart
          bestIdx = i
          bestReverse = false
        }
        if (distEnd < bestDist) {
          bestDist = distEnd
          bestIdx = i
          bestReverse = true
        }
      }

      if (bestIdx === -1) break
      const seg = remaining.splice(bestIdx, 1)[0]
      const ordered = bestReverse ? seg.reverse() : seg
      ring.push(...ordered.slice(1))
    }

    rings.push(ring)
  }

  return rings
}

/**
 * Legacy wrapper — stitches all segments into a single ring.
 * Kept for backward compatibility but prefer assembleClosedRings.
 */
export function assembleRings(segments: Array<[number, number][]>): [number, number][] {
  if (segments.length === 0) return []
  const rings = assembleClosedRings(segments)
  if (rings.length === 1) return rings[0]
  const result = rings[0]
  for (let i = 1; i < rings.length; i++) {
    result.push(...rings[i])
  }
  return result
}

function isClosed(ring: [number, number][]): boolean {
  return (
    ring.length >= 4 &&
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
  )
}

/**
 * Find which outer ring contains a given inner ring by testing
 * whether the first point of the inner ring is inside each outer ring.
 * Uses ray-casting point-in-polygon test.
 */
function findContainingRing(
  outerRings: Array<[number, number][]>,
  inner: [number, number][]
): number {
  if (outerRings.length <= 1) return 0
  if (inner.length === 0) return 0

  const [px, py] = inner[0]

  for (let idx = 0; idx < outerRings.length; idx++) {
    if (pointInRing(px, py, outerRings[idx])) return idx
  }

  return -1
}

function pointInRing(x: number, y: number, ring: [number, number][]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0],
      yi = ring[i][1]
    const xj = ring[j][0],
      yj = ring[j][1]
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}
